import React, { useMemo, useEffect } from 'react'
import { ModalTemplate, Title } from '@/ui'
import { useTranslation } from 'react-i18next'
import styles from './StaffingPositionForm.module.scss'
import { Formik } from 'formik'
import { Form } from './Form'
import { useDispatch, useSelector } from 'react-redux'
import { handbookSelectors } from '@/entity/handbook'
import { staffingActions, staffingSelectors, staffingThunks, staffThunks } from '@/entity'
import { ValidationSchema } from './ValidationSchema'
import { useDigitalSign } from '@/shared'
import { toNumber } from '@/utils/number'

export const StaffingPositionForm = ({ initialValues, onClose, actions, workPlaceRowSelect }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const positions = useSelector(handbookSelectors.positions)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)

	const options = useMemo(() => ({
		positions,
		positionsByKnz,
	}), [positions, positionsByKnz])
	const { successPayload, onSign, failedPayload, onClearPayload } = useDigitalSign()
	const employeeEditXML = useSelector(staffingSelectors.employeeEditXML)
	const { activeCompany, staffExperience, staffLevel, staffUnit } = useSelector((state) => ({
		activeCompany: state.company.company,
		staffExperience: state.staff.staffExperience,
		staffLevel: state.staff.staffLevel,
		staffUnit: state.staff.staffUnit,
	}))

	useEffect(() => {
		if (activeCompany?.msu) {
			let body = {
				data: workPlaceRowSelect.dstaffCategory.code,
			}
			dispatch(staffThunks.getStaffExperience(body))
			dispatch(staffThunks.getStaffUnit(body))
		}
	}, [])

	useEffect(() => {
		failedPayload && onClear()
	}, [failedPayload])

	useEffect(() => employeeEditXML && onSign(employeeEditXML), [employeeEditXML])

	useEffect(() => {
		if (successPayload) {
			dispatch(staffingThunks.sendEditWorkPlaceEsutd(initialValues.workPlaceId, { signXml: successPayload }))
			onClose()
		}
	}, [successPayload])

	const onClear = () => {
		onClearPayload()
		dispatch(staffingActions.setEditEmployeeXml(null))
	}

	const findObject = (arr = [], code) => {
		return arr?.find((i) => i.code == code)
	}

	const preparedOnSubmit = async (data) => {
		const {
			externalId,
			contractExternalId,
			factSalary,
			grossSalary,
			salarySupplement,
			iin,
			fullName,
			tariffRate,
			beginDate,
			endDate,
			reasonForAbsence,
			workPlaceId,
			substitutionExternalId,
			isCombining,
			positionCode,
			establishedPost,
			delegatingOrgBin,
		} = data

		//Добавлена трансформация объекта назначения, чтобы отправлять только нужные поля
		let transformedWorkPlace = {
			externalId,
			contractExternalId,
			factSalary: toNumber(data.factSalary),
			grossSalary,
			salarySupplement: toNumber(data.salarySupplement),
			iin,
			fullName,
			tariffRate,
			beginDate,
			endDate,
			reasonForAbsence,
			workPlaceId,
			substitutionExternalId,
			isCombining,
			positionCode: positionCode ? positionCode : workPlaceRowSelect?.dposition?.code,
			establishedPost: establishedPost ? establishedPost : workPlaceRowSelect?.establishedPost,
			delegatingOrgBin,
			...(activeCompany?.msu
				? {
						experienceCode: data.experienceCode,
						unitCode: data.unitCode,
						levelCode: data.levelCode,
				  }
				: {}),
		}
		if (initialValues.statusCode === 'SENT') {
			if (activeCompany?.msu) {
				let body = {
					...transformedWorkPlace,
					experience: {
						code: transformedWorkPlace?.experienceCode,
						nameRu: findObject(staffExperience.data || [], transformedWorkPlace?.experienceCode)?.rus,
						nameKz: findObject(staffExperience.data || [], transformedWorkPlace?.experienceCode)?.kz,
					},
					unit: {
						code: transformedWorkPlace?.unitCode,
						nameRu: findObject(staffUnit.data || [], transformedWorkPlace?.unitCode)?.rus,
						nameKz: findObject(staffUnit.data || [], transformedWorkPlace?.unitCode)?.kz,
					},
					level: {
						code: transformedWorkPlace?.levelCode,
						nameRu: findObject(staffLevel.data || [], transformedWorkPlace?.levelCode)?.rus,
						nameKz: findObject(staffLevel.data || [], transformedWorkPlace?.levelCode)?.kz,
					},
				}

				dispatch(staffingThunks.saveWorkPlaceEsutd(body))
			} else {
				dispatch(staffingThunks.saveWorkPlaceEsutd(transformedWorkPlace))
			}
		} else {
			onSubmit(transformedWorkPlace)
		}
	}

	const onSubmit = (transformedWorkPlace) => {
		let {workPlaceId, ...body} = transformedWorkPlace
		dispatch(staffingThunks.saveWorkPlace(body, workPlaceRowSelect.externalId))
		onClose()
	}

	return (
		<ModalTemplate onClose={onClose} priority={actions.edit || actions.create ? 2 : 1}>
			<div className={`scroll ${styles.wrapper}`}>
				<div className={styles.mt10}>
					<Title color="black">
						{actions.edit
							? `${t('workplace.edit_position')} ${t('staff_modal.title_position')}`
							: t('staff_modal.title_position')}
					</Title>
					<Formik
						validationSchema={ValidationSchema(workPlaceRowSelect)}
						initialValues={initialValues}
						options={options}
						onSubmit={preparedOnSubmit}
					>
						{
							({errors, values}) => {
								return <Form actions={actions} onClose={onClose} workPlaceRowSelect={workPlaceRowSelect}></Form>
							}
						}

					</Formik>
				</div>
			</div>
		</ModalTemplate>
	)
}
